import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

export const DebugStripe: React.FC = () => {
  const { session } = useAuth();
  const [debugData, setDebugData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [webhookTest, setWebhookTest] = useState<any>(null);

  const fetchDebugData = async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/billing/debug`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
      } else {
        console.error('Debug fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Debug fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const testWebhook = async () => {
    try {
      const response = await fetch(`${API_BASE}/webhook/stripe/test`);
      const data = await response.json();
      setWebhookTest(data);
    } catch (error: any) {
      console.error('Webhook test error:', error);
      setWebhookTest({ error: error.message });
    }
  };

  const syncStripe = async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/billing/subscription/sync-stripe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Sync successful! Check console for details.');
        console.log('Sync result:', data);
        await fetchDebugData();
      } else {
        const error = await response.json();
        alert(`Sync failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert('Sync failed - check console');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebugData();
    testWebhook();
  }, [session]);

  if (!debugData && !loading) {
    return <div className="p-4">Loading debug data...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold mb-6">Stripe Debug Information</h1>
        
        {/* Quick Actions */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={fetchDebugData}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 inline mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </button>
          
          <button
            onClick={syncStripe}
            disabled={loading}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            Sync with Stripe
          </button>
          
          <button
            onClick={testWebhook}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            Test Webhook Endpoint
          </button>
        </div>

        {/* User Info */}
        {debugData?.user && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h2 className="font-semibold mb-2">User Information</h2>
            <p>ID: <code className="bg-gray-200 px-1 rounded">{debugData.user.id}</code></p>
            <p>Email: <code className="bg-gray-200 px-1 rounded">{debugData.user.email}</code></p>
          </div>
        )}

        {/* Database Subscription */}
        {debugData?.database_subscription && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h2 className="font-semibold mb-2">Database Subscription</h2>
            <pre className="text-xs overflow-auto bg-white p-2 rounded">
              {JSON.stringify(debugData.database_subscription, null, 2)}
            </pre>
          </div>
        )}

        {/* Stripe Customer */}
        {debugData?.stripe_customer && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <h2 className="font-semibold mb-2">Stripe Customer</h2>
            <p>ID: <code className="bg-gray-200 px-1 rounded">{debugData.stripe_customer.id}</code></p>
            <p>Email: {debugData.stripe_customer.email}</p>
            <p>Metadata: <code className="bg-gray-200 px-1 rounded">{JSON.stringify(debugData.stripe_customer.metadata)}</code></p>
          </div>
        )}

        {/* Stripe Subscriptions */}
        {debugData?.stripe_subscriptions && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg">
            <h2 className="font-semibold mb-2">Stripe Subscriptions ({debugData.stripe_subscriptions.length})</h2>
            {debugData.stripe_subscriptions.map((sub: any, idx: number) => (
              <div key={idx} className="mb-2 p-2 bg-white rounded">
                <p>ID: <code className="bg-gray-200 px-1 rounded text-xs">{sub.id}</code></p>
                <p>Status: <span className={`font-semibold ${sub.status === 'active' ? 'text-green-600' : 'text-gray-600'}`}>{sub.status}</span></p>
                <p>Price ID: <code className="bg-gray-200 px-1 rounded text-xs">{sub.price_id}</code></p>
                <p>Created: {new Date(sub.created).toLocaleString()}</p>
                <p>Period End: {new Date(sub.current_period_end).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* Environment Check */}
        {debugData?.environment_check && (
          <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
            <h2 className="font-semibold mb-2">Environment Variables</h2>
            {Object.entries(debugData.environment_check).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between py-1">
                <span className="font-mono text-sm">{key}:</span>
                <span className="flex items-center">
                  {typeof value === 'boolean' ? (
                    value ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />
                  ) : (
                    <code className="bg-gray-200 px-2 py-1 rounded text-xs">{String(value)}</code>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Webhook Test */}
        {webhookTest && (
          <div className="mb-6 p-4 bg-orange-50 rounded-lg">
            <h2 className="font-semibold mb-2">Webhook Test Result</h2>
            <pre className="text-xs overflow-auto bg-white p-2 rounded">
              {JSON.stringify(webhookTest, null, 2)}
            </pre>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="font-semibold mb-2 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
            Debugging Steps
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Check if all environment variables are set correctly (green checkmarks)</li>
            <li>Verify the Stripe customer ID matches between database and Stripe</li>
            <li>Check if there's an active subscription in Stripe</li>
            <li>Click "Sync with Stripe" to manually update your subscription</li>
            <li>For webhooks, ensure you're running: <code className="bg-gray-200 px-1 rounded">stripe listen --forward-to localhost:3002/webhook/stripe</code></li>
          </ol>
        </div>
      </div>
    </div>
  );
}; 